/**
 * Brief file ingest helpers — the ONE place the client turns a picked `File`
 * into the base64 + format the upload wire carries.
 *
 * Extracted from `<UploadDialog>` when `<NewArticleDialog>` became the second
 * consumer (RULE ZERO — a second `FileReader` + a second extension table is
 * exactly the drift that ships one dialog accepting `.markdown` and the other
 * silently rejecting it). Both dialogs now compose these.
 *
 * ── THE SIZE CAP IS LOAD-BEARING, AND IT IS NOT THE 2 MB ONE ────────────────
 * `src/api/limits.zig:21` sets a 2 MiB TRANSPORT cap on the ctesibiuz
 * `ServerConfig`, and its own comment reads "2 MB of base64 ≈ 1.5 MB of file".
 * That is NOT the binding cap for these uploads: the body is JSON, and
 * ctesibiuz's `parseJsonBody` rejects any JSON body over
 * `default_max_json_body_size` = 1 MiB (`libs/ctesibiuz/src/extractors.zig:103`)
 * BEFORE the transport cap is ever reached. base64 inflates by 4/3, so the real
 * ceiling on the raw file is ≈ (1 MiB − envelope) × ¾.
 *
 * {@link MAX_BRIEF_FILE_BYTES} is that number rounded down to 750 KiB, leaving
 * 16 KiB for the JSON envelope (title/slug/brand/lang/filename/flags):
 *   768,000 raw → 1,024,000 base64 + 16,384 envelope = 1,040,384 < 1,048,576. ✓
 *
 * Enforcing it CLIENT-side is a UX fix, not a security control — the server
 * still rejects an oversized body, and must. Without the client check the user
 * gets an opaque failure from a request that never reached a handler; with it
 * they get "that file is 2.1 MB — the limit is 750 KB".
 */

/** The canonical source formats the brief ingest pipeline understands
 *  (`src/seed/render.zig` renderBytes). */
export const BRIEF_FORMATS = ["md", "docx", "html"] as const;
export type BriefFormat = (typeof BRIEF_FORMATS)[number];

/** `accept` for the document library upload (docx / md / html). */
export const UPLOAD_ACCEPT = ".docx,.md,.markdown,.html,.htm";

/** `accept` for an ARTICLE BRIEF — the owner's two upload formats. An `.html`
 *  brief has no product story ("upload the brief either in docx or md format"),
 *  and offering it here would promise a path the create dialog does not name. */
export const BRIEF_ACCEPT = ".docx,.md,.markdown";

/** Max RAW bytes of a brief/upload source. See the header note — derived from
 *  ctesibiuz's 1 MiB JSON-body cap, NOT from the 2 MiB transport cap. */
export const MAX_BRIEF_FILE_BYTES = 750 * 1024;

/** Canonical source format from a filename extension; `""` when unsupported. */
export function formatFromName(name: string): BriefFormat | "" {
  const dot = name.lastIndexOf(".");
  if (dot < 0) return "";
  const ext = name.slice(dot + 1).toLowerCase();
  if (ext === "docx") return "docx";
  if (ext === "html" || ext === "htm") return "html";
  if (ext === "md" || ext === "markdown") return "md";
  return "";
}

/** Strip the extension from a filename to seed a default title. */
export function baseName(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(0, dot) : name;
}

/** Human byte size for an error the user can act on ("2.1 MB", "750 KB"). */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Validate a picked file against the accepted formats + the size cap. Returns a
 * TRUTHFUL, actionable message, or `null` when the file is fine.
 *
 * `accept` is the extension allowlist to police (default {@link BRIEF_ACCEPT}) —
 * the `<input accept>` attribute is a file-picker HINT the user can bypass with
 * drag-and-drop or "All files", so it is never the check.
 */
export function fileRejectReason(
  file: { name: string; size: number },
  opts: { formats?: readonly BriefFormat[]; maxBytes?: number } = {},
): string | null {
  const formats = opts.formats ?? (["docx", "md"] as const);
  const maxBytes = opts.maxBytes ?? MAX_BRIEF_FILE_BYTES;
  const format = formatFromName(file.name);
  if (!format || !formats.includes(format)) {
    const names = formats.map((f) => `.${f}`).join(" or ");
    return `“${file.name}” is not a ${names} file. Choose a ${names} document.`;
  }
  if (file.size === 0) {
    return `“${file.name}” is empty — there is no brief in it.`;
  }
  if (file.size > maxBytes) {
    return `“${file.name}” is ${formatBytes(file.size)} — the limit is ${formatBytes(
      maxBytes,
    )}. Split the brief or paste the text instead.`;
  }
  return null;
}

/** Read a `File` as a base64 payload (the `data:...;base64,` prefix is stripped;
 *  the backend tolerates it either way). */
export function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.readAsDataURL(file);
  });
}

/** UTF-8 → standard base64. `btoa` is Latin-1 only, so the text is encoded to
 *  bytes FIRST (`TextEncoder`) and the byte string is chunked — a naïve
 *  `btoa(text)` throws on the very first "ä" a German brief contains, and
 *  `String.fromCharCode(...bytes)` blows the argument limit on a long one. */
export function utf8ToBase64(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

/** Standard base64 → UTF-8 text (the inverse of {@link utf8ToBase64}; used by
 *  tests and by any caller that needs to read back what it encoded). */
export function base64ToUtf8(b64: string): string {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}
